/**
 * Web Vitals Reporting Utility
 * 
 * Reports Core Web Vitals metrics to analytics/monitoring service.
 * Tracks: LCP, FID, CLS, FCP, TTFB
 * 
 * Usage:
 * import reportWebVitals from './utils/reportWebVitals';
 * reportWebVitals(console.log); // or send to analytics
 */

import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';
import { createLogger } from './logger';

const logger = createLogger('web-vitals');

/**
 * Report Web Vitals to analytics service
 * @param {Function} onPerfEntry - Callback to handle metric data
 */
const reportWebVitals = (onPerfEntry) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    getCLS(onPerfEntry);
    getFID(onPerfEntry);
    getFCP(onPerfEntry);
    getLCP(onPerfEntry);
    getTTFB(onPerfEntry);
  }
};

/**
 * Send metrics to analytics endpoint
 * @param {Object} metric - Web Vitals metric object
 */
export const sendToAnalytics = (metric) => {
  const { name, value, id, delta } = metric;
  
  if (process.env.NODE_ENV === 'development') {
    logger.info(`[${name}]`, {
      value: Math.round(value),
      delta: Math.round(delta),
      id,
      rating: getRating(name, value)
    });
  }
  
  if (process.env.NODE_ENV === 'production' && window.gtag) {
    window.gtag('event', name, {
      event_category: 'Web Vitals',
      value: Math.round(name === 'CLS' ? value * 1000 : value),
      event_label: id,
      non_interaction: true,
    });
  }
  
  if (process.env.NODE_ENV === 'production') {
    const body = JSON.stringify({
      metric: name,
      value: Math.round(value),
      delta: Math.round(delta),
      id,
      rating: getRating(name, value),
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
    
    try {
      if (navigator.sendBeacon) {
        // Fix 415 Error: Wrap body in Blob with correct content type
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon('/api/v1/analytics/web-vitals', blob);
      } else {
        fetch('/api/v1/analytics/web-vitals', {
          method: 'POST',
          body,
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        }).catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            logger.error('Failed to send web vitals', error);
          }
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('Failed to send web vitals', error);
      }
    }
  }
};

/**
 * Get performance rating based on metric thresholds
 * @param {string} name - Metric name
 * @param {number} value - Metric value
 * @returns {string} - Rating: 'good', 'needs-improvement', or 'poor'
 */
const getRating = (name, value) => {
  const thresholds = {
    CLS: { good: 0.1, poor: 0.25 },
    FID: { good: 100, poor: 300 },
    FCP: { good: 1800, poor: 3000 },
    LCP: { good: 2500, poor: 4000 },
    TTFB: { good: 800, poor: 1800 },
  };
  
  const threshold = thresholds[name];
  if (!threshold) return 'unknown';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
};

/**
 * Initialize Web Vitals monitoring
 * Call this in your app entry point (index.js)
 */
export const initWebVitals = () => {
  reportWebVitals(sendToAnalytics);
  
  logger.info('Web Vitals monitoring initialized');
};

export default reportWebVitals;
